import { useEffect, useState } from "react"
import { flushSync } from "react-dom"
import { GlobalStyles } from "@mui/material"

import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../modules/localization/useDateTime"

import { getPrintFooterBoxStyles, getPrintHeaderBoxStyles, getPrintOrderPageName } from "./printMarginBoxes"

const PAGE_TOKEN = "__page__"
const PAGES_TOKEN = "__pages__"
const PRINT_DIALOG = ".MuiDialog-root:has(#printable-content)"

function toPageCounterContent(text: string) {
    return JSON.stringify(text).replace(PAGE_TOKEN, '" counter(page) "').replace(PAGES_TOKEN, '" counter(pages) "')
}

function usePrintedAt() {
    const dateTimeFactory = useDateTime()
    const [printedAt, setPrintedAt] = useState(() => dateTimeFactory.now())

    useEffect(() => {
        const handleBeforePrint = () => {
            flushSync(() => setPrintedAt(dateTimeFactory.now()))
        }

        window.addEventListener("beforeprint", handleBeforePrint)
        return () => window.removeEventListener("beforeprint", handleBeforePrint)
    }, [dateTimeFactory])

    return printedAt
}

type PrintPageHeaderFooterProps = Readonly<{
    title: string
    kitchen?: string
    orderIds?: ReadonlyArray<number>
}>

export function PrintPageHeaderFooter({ title, kitchen = "", orderIds = [] }: PrintPageHeaderFooterProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const printedAt = usePrintedAt()
    const printedLabel = l10n.getString("print-page-header-printed-label", {
        title,
        date: dateTimeFactory.formatDate(printedAt),
        time: dateTimeFactory.formatTime(printedAt),
    })
    const pageCounter = l10n.getString("print-page-footer-page-counter", { page: PAGE_TOKEN, pages: PAGES_TOKEN })
    const getOrderLabel = (orderId: number) =>
        l10n.getString("print-page-footer-order-label", { orderId: orderId.toString() })

    return (
        <GlobalStyles
            styles={(theme) => {
                const headerBox = getPrintHeaderBoxStyles(theme)
                const footerBox = getPrintFooterBoxStyles(theme)
                const emphasizedColor = theme.palette.tailwind.slate[800]

                return {
                    "@page": {
                        // small enough that Chrome drops its own date, title, url and page number
                        marginTop: "2rem",
                        marginBottom: "2rem",
                        "@top-left": {
                            ...headerBox,
                            content: JSON.stringify(kitchen),
                            color: emphasizedColor,
                            fontWeight: 700,
                            letterSpacing: ".06rem",
                            textTransform: "uppercase",
                        },
                        "@top-right": { ...headerBox, content: JSON.stringify(printedLabel) },
                        // generated even when empty, otherwise the line has a gap left of the centre box
                        "@bottom-left": {
                            ...footerBox,
                            content: JSON.stringify(orderIds.length === 1 ? getOrderLabel(orderIds[0]) : ""),
                        },
                        "@bottom-right": {
                            ...footerBox,
                            content: toPageCounterContent(pageCounter),
                            color: emphasizedColor,
                            fontWeight: 500,
                        },
                    },
                    ...(orderIds.length > 1 && {
                        ...Object.fromEntries(
                            orderIds.map((orderId) => [
                                `@page ${getPrintOrderPageName(orderId)}`,
                                { "@bottom-left": { ...footerBox, content: JSON.stringify(getOrderLabel(orderId)) } },
                            ]),
                        ),
                        "@media print": {
                            // Chrome ignores page names in out-of-flow content; MUI makes the dialog absolute in print
                            [PRINT_DIALOG]: {
                                position: "static !important",
                            },
                            // in flow, the 210mm body from index.css is wider than the page and Chrome shrinks everything
                            "html:has(#printable-content), body:has(#printable-content)": {
                                width: "auto !important",
                            },
                            // anything else in flow stays on the default page and forces blank pages around the orders
                            [`body:has(#printable-content) > :not(${PRINT_DIALOG}), ${PRINT_DIALOG} > [data-testid^='sentinel']`]:
                                {
                                    display: "none !important",
                                },
                        },
                    }),
                }
            }}
        />
    )
}
